import React, { createContext, useContext, useState, useEffect } from 'react';
import { can, CORE_OWNER_AREAS, userCan } from './roles';
import { authMe } from '../api';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } });
  // Owners can switch the active branch; everyone else is pinned to their own.
  const [activeBranch, setActiveBranch] = useState(() => {
    const stored = localStorage.getItem('active_branch');
    return stored ? Number(stored) : null;
  });

  // On load, refresh plan features + perms from the server so super-admin
  // package/feature changes apply without a manual logout/login.
  useEffect(() => {
    if (!localStorage.getItem('token')) return;
    authMe().then(r => {
      if (r.data.token) localStorage.setItem('token', r.data.token);
      setUser(u => {
        if (!u) return u;
        const nu = { ...u, email: r.data.email || u.email, features: r.data.features, perms: r.data.perms, license: r.data.license };
        localStorage.setItem('user', JSON.stringify(nu));
        return nu;
      });
    }).catch(() => {});
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('active_branch', String(userData.branch_id || 1));
    setUser(userData);
    setActiveBranch(userData.branch_id || 1);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('active_branch');
    setUser(null);
    setActiveBranch(null);
  };

  // Owner-only: change the branch all API calls are scoped to.
  const switchBranch = (branchId) => {
    if ((user?.role || '').toLowerCase() !== 'owner') return;
    localStorage.setItem('active_branch', String(branchId));
    setActiveBranch(Number(branchId));
  };

  const role = (user?.role || '').toLowerCase();
  const hasAccess = (area) => userCan(user, area, 'view');
  // can(area, action) — granular per-action check for hiding buttons.
  const canDo = (area, action = 'view') => userCan(user, area, action);

  return (
    <AuthContext.Provider value={{
      user, role, login, logout,
      isLoggedIn: !!localStorage.getItem('token'),
      hasAccess, can: canDo,
      branchId: activeBranch || user?.branch_id || 1,
      branchName: user?.branch_name || 'Main Branch',
      switchBranch,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
